/**
 * Catalog invariants, checked as data rather than trusted.
 *
 * The catalog is admin-curated and hand-edited, so the honest failure mode is a typo in a
 * row rather than a malicious write. `validateMedium` returns every problem it finds so an
 * admin sees the whole list at once, instead of fixing them one refresh at a time.
 */
const PRINT_FORMS = [
    'codex',
    'leporello',
    'stack',
    'tunnel',
    'stage',
    'strip',
    'disc',
];
function isPositiveInteger(value) {
    return typeof value === 'number' && Number.isInteger(value) && value > 0;
}
function isPositive(value) {
    return typeof value === 'number' && Number.isFinite(value) && value > 0;
}
function gcd(a, b) {
    let x = a;
    let y = b;
    while (y !== 0)
        [x, y] = [y, x % y];
    return x;
}
/**
 * The print block is geometry, so every number in it has to be one a printer could use.
 *
 * `frame` is held to the engine's own rule — reduced, positive integers — because it is the
 * one field that reaches a scene's content address, and two spellings of one ratio must not
 * become two catalog facts.
 */
function checkPrint(print, problems) {
    if (!PRINT_FORMS.includes(print.form)) {
        problems.push(`print.form must be one of ${PRINT_FORMS.join(' | ')} (got ${JSON.stringify(print.form)})`);
    }
    if (print.frame !== undefined) {
        const { w, h } = print.frame;
        if (!isPositiveInteger(w) || !isPositiveInteger(h)) {
            problems.push(`print.frame must be two positive integers (got ${w}:${h})`);
        }
        else if (gcd(w, h) !== 1) {
            problems.push(`print.frame must be reduced (got ${w}:${h}, which is ${w / gcd(w, h)}:${h / gcd(w, h)})`);
        }
    }
    if (print.unit !== undefined) {
        const { trimMm, bleedMm, safeMm } = print.unit;
        if (!isPositive(trimMm?.w) || !isPositive(trimMm?.h)) {
            problems.push('print.unit.trimMm must be a positive width and height');
        }
        if (!(typeof bleedMm === 'number' && Number.isFinite(bleedMm) && bleedMm >= 0)) {
            problems.push('print.unit.bleedMm must be zero or more');
        }
        if (!(typeof safeMm === 'number' && Number.isFinite(safeMm) && safeMm >= 0)) {
            problems.push('print.unit.safeMm must be zero or more');
        }
    }
}
/** The youngest and oldest the whole product serves (PRD 03 §4.1). */
export const AGE_FLOOR = 2;
export const AGE_CEILING = 7;
/** Lowercase slug: letters, digits, single hyphens, no leading or trailing hyphen. */
export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
function isNonEmptyText(value) {
    return typeof value === 'string' && value.trim().length > 0;
}
function checkComposition(composition, problems) {
    const { min, ideal, max } = composition.scenes;
    if (!(min <= ideal && ideal <= max)) {
        problems.push(`composition.scenes must satisfy min <= ideal <= max (got ${min}/${ideal}/${max})`);
    }
    if (min < 1) {
        problems.push(`composition.scenes.min must be at least 1 (got ${min})`);
    }
    if (composition.outputs.length === 0) {
        problems.push('composition.outputs must name at least one output kind');
    }
    if (composition.pieces !== undefined && composition.input !== 'layers') {
        problems.push("composition.pieces only means something when input is 'layers'");
    }
}
/**
 * Every problem with a medium record, as readable lines. An empty array means it is sound.
 */
export function validateMedium(medium) {
    const problems = [];
    if (!SLUG_PATTERN.test(medium.id)) {
        problems.push(`id must be a lowercase slug (got ${JSON.stringify(medium.id)})`);
    }
    if (!isNonEmptyText(medium.name.en)) {
        problems.push('name.en must not be empty');
    }
    if (!isNonEmptyText(medium.physicalAction.en)) {
        problems.push('physicalAction.en must not be empty — every medium is something hands do');
    }
    if (!Number.isFinite(medium.order)) {
        problems.push('order must be a finite number');
    }
    if (medium.ageMin < AGE_FLOOR || medium.ageMax > AGE_CEILING) {
        problems.push(`age band must sit within ${AGE_FLOOR}–${AGE_CEILING} (got ${medium.ageMin}–${medium.ageMax})`);
    }
    if (medium.ageMin > medium.ageMax) {
        problems.push(`ageMin must not exceed ageMax (got ${medium.ageMin}–${medium.ageMax})`);
    }
    // A ruled-out format keeps its place in the catalog, but it owes the reader a reason.
    const ruledOut = medium.ruledOut === true;
    if (ruledOut && !isNonEmptyText(medium.ruledOutReason?.en)) {
        problems.push('a ruled-out medium must say why (ruledOutReason)');
    }
    if (ruledOut !== (medium.tier === null)) {
        problems.push(`tier must be null exactly when ruledOut is true (tier=${String(medium.tier)}, ruledOut=${String(ruledOut)})`);
    }
    // The five platform plays never render to paper, so they carry no composition. Film is
    // the one digital medium that does render — of the locked layers, into a video.
    const isFilm = medium.composition?.layout === 'film';
    if (medium.family === 'digital') {
        if (medium.composition !== undefined && !isFilm) {
            problems.push('a digital medium carries no composition unless it is the film');
        }
    }
    else if (medium.composition === undefined) {
        problems.push('a non-digital medium must carry a composition — that is how it is rendered');
    }
    // The store's format chip and the renderer are the same fact from two sides: a medium that
    // renders has a format to name, and a platform play has neither.
    const hasFormatLabel = isNonEmptyText(medium.formatLabel?.en);
    if (medium.composition !== undefined && !hasFormatLabel) {
        problems.push('a medium with a composition must carry a formatLabel for the store chip');
    }
    if (medium.composition === undefined && hasFormatLabel) {
        problems.push('formatLabel belongs only on a medium that renders (one with a composition)');
    }
    if (medium.composition !== undefined) {
        checkComposition(medium.composition, problems);
    }
    // Print geometry belongs to a physical thing. A digital medium (the platform plays, and
    // film) has no paper, so a print block on one is a mistake rather than a plan.
    if (medium.print !== undefined) {
        if (medium.family === 'digital') {
            problems.push('a digital medium carries no print block — there is nothing to print');
        }
        checkPrint(medium.print, problems);
    }
    return problems;
}
/** True when the record has no problems at all. */
export function isValidMedium(medium) {
    return validateMedium(medium).length === 0;
}
//# sourceMappingURL=validate.js.map